import { cn } from '@/lib/utils'

interface ChartTooltipProps {
  /** Heading line — the thing being pointed at. */
  label: string
  /** Body rows, in reading order. A null value is skipped. */
  rows: { text: string; tone?: string }[]
  /** Horizontal anchor, as a percentage of the plot's width. */
  left: number
  /** Vertical anchor, as a percentage of the plot's height. Defaults to the top. */
  top?: number
  /**
   * Which edge the card hangs from. `center` is the default; the ends flip so a
   * card never runs off the side of its plot.
   */
  align?: 'start' | 'center' | 'end'
  className?: string
}

/**
 * The readout card the charts share on hover.
 *
 * Every chart on these pages used a native `title` attribute, which the browser
 * renders after roughly a second, in the OS's own styling, with no control over
 * what it says beyond one line of text. On a dashboard where the whole point is
 * reading a value off a bar, a one-second delay is the difference between a
 * chart you can scan and one you have to wait on.
 *
 * Deliberately HTML rather than SVG: the plots use `preserveAspectRatio="none"`,
 * which stretches the viewBox and would skew any text drawn inside it.
 *
 * `pointer-events-none` matters — a card that sat under the cursor would take
 * the mouseleave and flicker the whole thing off and on again.
 */
export function ChartTooltip({
  label,
  rows,
  left,
  top,
  align = 'center',
  className,
}: ChartTooltipProps) {
  const shift =
    align === 'start' ? 'translateX(0)' : align === 'end' ? 'translateX(-100%)' : 'translateX(-50%)'

  return (
    <div
      role="tooltip"
      className={cn(
        'border-line bg-surface-2 text-fg pointer-events-none absolute z-20 rounded-lg border',
        'px-2.5 py-1.5 font-mono text-[10px] whitespace-nowrap shadow-lg shadow-black/30',
        className,
      )}
      style={{
        left: `${left}%`,
        top: top === undefined ? 0 : `${top}%`,
        transform: top === undefined ? shift : `${shift} translateY(-115%)`,
      }}
    >
      <p className="text-fg-subtle tracking-[0.08em] uppercase">{label}</p>
      {rows.map((row) => (
        <p key={row.text} className={cn('mt-0.5', row.tone ?? 'text-fg-muted')}>
          {row.text}
        </p>
      ))}
    </div>
  )
}
